import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AuthContext from "../../context/AuthContext/AuthContext";
import api_endpoints from "../../api";

function cloverSdkUrl() {
  const env = (process.env.REACT_APP_CLOVER_ENV || "sandbox").toLowerCase();
  return env === "production"
    ? "https://checkout.clover.com/sdk.js"
    : "https://checkout.sandbox.dev.clover.com/sdk.js";
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some((s) => s.src === src)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

function useClover(organizationId) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const cloverRef = useRef(null);
  const cardEls = useRef({});
  const createdEls = useRef([]);

  useEffect(() => {
    if (!organizationId) {
      setReady(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        await loadScript(cloverSdkUrl());
        if (!window.Clover) throw new Error("Clover SDK missing");

        const { apiAccessKey, merchantId } = await api_endpoints.getCloverPakms({
          organization_id: organizationId,
        });

        const options = merchantId ? { merchantId } : undefined;
        const clover = new window.Clover(apiAccessKey, options);
        cloverRef.current = clover;

        const elements = clover.elements();
        const styles = {
          body: {
            fontFamily:
              "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
            fontSize: "14px",
          },
          input: { fontSize: "14px" },
        };

        cardEls.current.number = elements.create("CARD_NUMBER", styles);
        cardEls.current.date = elements.create("CARD_DATE", styles);
        cardEls.current.cvv = elements.create("CARD_CVV", styles);
        cardEls.current.postal = elements.create("CARD_POSTAL_CODE", styles);
        createdEls.current = Object.values(cardEls.current);

        if (!cancelled) {
          setError(null);
          setReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e);
          setReady(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      createdEls.current.forEach((el) => {
        try {
          el?.unmount?.();
        } catch {}
      });
      createdEls.current = [];
      cardEls.current = {};
      cloverRef.current = null;
    };
  }, [organizationId]);

  return { ready, error, cloverRef, cardEls };
}

function CloverField({ label, fieldId }) {
  return (
    <FormControl fullWidth>
      <InputLabel shrink sx={{ mb: 0.5, lineHeight: 1 }}>
        {label}
      </InputLabel>
      <Box
        id={fieldId}
        sx={(theme) => ({
          mt: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 1.25,
          height: 40,
          display: "flex",
          alignItems: "center",
          transition: "border-color 120ms ease, box-shadow 120ms ease",
          "&:focus-within": {
            borderColor: "primary.main",
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
          },
          "& iframe": { width: "100%", height: "100%" },
          "& > div": { width: "100%", height: "100%" },
        })}
      />
    </FormControl>
  );
}

export default function OneTimeChargeTest() {
  const authContext = useContext(AuthContext);
  const isSuperUser = !!authContext?.authContext?.accessToken?.is_super_user;
  const tokenOrganizationId = authContext?.authContext?.accessToken?.organization_id;

  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(
    tokenOrganizationId || localStorage.getItem("selectedOrganisationId") || ""
  );

  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("Clover one-time test charge");
  const [customerFirstName, setCustomerFirstName] = useState("");
  const [customerLastName, setCustomerLastName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [error, setError] = useState("");
  const [debugPayload, setDebugPayload] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = v1/charges, 1 = v1/customers
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [charging, setCharging] = useState(false);
  const [customerResponse, setCustomerResponse] = useState(null);
  const [chargeResponse, setChargeResponse] = useState(null);

  const clover = useClover(selectedOrganizationId);

  const mountIds = {
    number: "clover-test-card-number",
    date: "clover-test-card-date",
    cvv: "clover-test-card-cvv",
    postal: "clover-test-card-postal",
  };

  const mountedRef = useRef(false);

  useEffect(() => {
    if (!isSuperUser) return;

    let active = true;
    (async () => {
      try {
        setOrganizationsLoading(true);
        const response = await api_endpoints.getOrganizations("");
        if (!active) return;
        setOrganizations(Array.isArray(response?.data) ? response.data : []);
      } catch {
        if (!active) return;
        setOrganizations([]);
      } finally {
        if (active) setOrganizationsLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [isSuperUser]);

  useEffect(() => {
    if (!isSuperUser || !tokenOrganizationId || selectedOrganizationId) return;
    setSelectedOrganizationId(tokenOrganizationId);
  }, [isSuperUser, tokenOrganizationId, selectedOrganizationId]);

  useEffect(() => {
    mountedRef.current = false;
  }, [selectedOrganizationId]);

  useEffect(() => {
    if (!clover.ready || mountedRef.current) return;

    const { number, date, cvv, postal } = clover.cardEls.current || {};
    try {
      if (!number || !date || !cvv || !postal) {
        throw new Error("Clover fields are not ready");
      }

      number.mount(`#${mountIds.number}`);
      date.mount(`#${mountIds.date}`);
      cvv.mount(`#${mountIds.cvv}`);
      postal.mount(`#${mountIds.postal}`);
      mountedRef.current = true;
    } catch (e) {
      setError(e?.message || "Failed to mount Clover fields");
    }
  }, [clover.ready, clover.cardEls, mountIds.number, mountIds.date, mountIds.cvv, mountIds.postal]);

  const handleCharge = useCallback(async () => {
    try {
      setError("");
      setDebugPayload(null);
      setChargeResponse(null);
      setCustomerResponse(null);

      if (!isSuperUser) {
        setError("This page is limited to super admins.");
        return;
      }

      if (!selectedOrganizationId) {
        setError("Select an organization first.");
        return;
      }

      const amountNumber = Number(amount);
      if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
        setError("Amount must be a positive number.");
        return;
      }

      const amountCents = Math.round(amountNumber * 100);
      if (!clover.ready || !mountedRef.current) {
        setError("Clover fields are not ready.");
        return;
      }

      setCharging(true);
      const clv = clover.cloverRef.current;
      if (!clv) throw new Error("Clover SDK not initialized");

      const tokenRes = await clv.createToken();
      if (tokenRes?.errors) {
        const msg = Object.values(tokenRes.errors).join(" ");
        throw new Error(msg || "Card tokenization failed");
      }

      const source = tokenRes?.token;
      if (!source) {
        throw new Error("Missing Clover source token");
      }

      const response = await api_endpoints.createOneTimeChargeTest({
        organization_id: selectedOrganizationId,
        amount: amountCents,
        source,
        description,
      });

      setChargeResponse(response);
    } catch (e) {
      setDebugPayload(e?.response?.data || null);
      setError(e?.response?.data?.message || e?.message || "Charge failed");
    } finally {
      setCharging(false);
    }
  }, [isSuperUser, selectedOrganizationId, amount, clover.ready, clover.cloverRef, description]);

  const handleCreateCustomer = useCallback(async () => {
    try {
      setError("");
      setDebugPayload(null);
      setCustomerResponse(null);
      setChargeResponse(null);

      if (!isSuperUser) {
        setError("This page is limited to super admins.");
        return;
      }

      if (!selectedOrganizationId) {
        setError("Select an organization first.");
        return;
      }

      if (!clover.ready || !mountedRef.current) {
        setError("Clover fields are not ready.");
        return;
      }

      setCreatingCustomer(true);
      const clv = clover.cloverRef.current;
      if (!clv) throw new Error("Clover SDK not initialized");

      const tokenRes = await clv.createToken();
      if (tokenRes?.errors) {
        const msg = Object.values(tokenRes.errors).join(" ");
        throw new Error(msg || "Card tokenization failed");
      }

      const source = tokenRes?.token;
      if (!source) {
        throw new Error("Missing Clover source token");
      }

      const response = await api_endpoints.createOneTimeCustomerTest({
        organization_id: selectedOrganizationId,
        source,
        firstName: customerFirstName,
        lastName: customerLastName,
        email: customerEmail,
        phone: customerPhone,
      });

      setCustomerResponse(response);
    } catch (e) {
      setDebugPayload(e?.response?.data || null);
      setError(e?.response?.data?.message || e?.message || "Customer creation failed");
    } finally {
      setCreatingCustomer(false);
    }
  }, [
    isSuperUser,
    selectedOrganizationId,
    clover.ready,
    clover.cloverRef,
    customerFirstName,
    customerLastName,
    customerEmail,
    customerPhone,
  ]);

  const handleOrganizationChange = useCallback(
    (event) => {
      const orgId = event.target.value;
      setSelectedOrganizationId(orgId);
      setError("");
      setDebugPayload(null);
      setCustomerResponse(null);
      setChargeResponse(null);

      if (isSuperUser) {
        authContext?.authContext?.setSuperUserOrganization?.(orgId);
        if (orgId) {
          localStorage.setItem("selectedOrganisationId", orgId);
          const selectedOrg = organizations.find((org) => org?._id === orgId);
          if (selectedOrg?.legal_name) {
            localStorage.setItem("selectedOrganisationName", selectedOrg.legal_name);
          }
        } else {
          localStorage.removeItem("selectedOrganisationId");
          localStorage.removeItem("selectedOrganisationName");
        }
      }
    },
    [isSuperUser, authContext, organizations]
  );

  const customerRequestView = customerResponse?.backendRequest || debugPayload?.backendRequest || null;
  const customerFullResponseView = customerResponse || debugPayload || null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
        Clover One-Time Test Charge
      </Typography>

      {!isSuperUser && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This page is limited to super admins.
        </Alert>
      )}

      {!selectedOrganizationId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Select an organization before charging.
        </Alert>
      )}

      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!!clover.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {clover.error?.message || String(clover.error)}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label="v1/charges" />
          <Tab label="v1/customers" />
        </Tabs>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Organization"
              fullWidth
              size="small"
              select
              value={selectedOrganizationId}
              onChange={handleOrganizationChange}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              disabled={!isSuperUser || organizationsLoading}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            >
              <option value="">Select organization</option>
              {organizations.map((org) => (
                <option key={org._id} value={org._id}>
                  {org.legal_name}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6} />

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">
              Card form (shared by both tabs)
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Amount (CAD)"
              fullWidth
              size="small"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: "0", step: "0.01" }}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Description"
              fullWidth
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>

          {activeTab === 1 ? (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Customer payload fields (for Clover v1/customers)
                </Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="First name"
                  fullWidth
                  size="small"
                  value={customerFirstName}
                  onChange={(e) => setCustomerFirstName(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Last name"
                  fullWidth
                  size="small"
                  value={customerLastName}
                  onChange={(e) => setCustomerLastName(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Email"
                  fullWidth
                  size="small"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Phone"
                  fullWidth
                  size="small"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <CloverField label="Card number" fieldId={mountIds.number} />
          </Grid>
          <Grid item xs={12} md={4}>
            <CloverField label="Expiry (MM/YY)" fieldId={mountIds.date} />
          </Grid>
          <Grid item xs={12} md={4}>
            <CloverField label="CVC" fieldId={mountIds.cvv} />
          </Grid>
          <Grid item xs={12} md={4}>
            <CloverField label="Postal code" fieldId={mountIds.postal} />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {activeTab === 0 ? (
                <Button
                  variant="contained"
                  onClick={handleCharge}
                  disabled={charging || creatingCustomer || !clover.ready || !mountedRef.current || !isSuperUser || !selectedOrganizationId}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {charging ? "Charging..." : "Create charge (v1/charges)"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleCreateCustomer}
                  disabled={creatingCustomer || charging || !clover.ready || !mountedRef.current || !isSuperUser || !selectedOrganizationId}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {creatingCustomer ? "Creating customer..." : "Create customer (v1/customers)"}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {activeTab === 1 && customerResponse && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Customer created successfully (id: {customerResponse?.customer?.id || "n/a"}).
        </Alert>
      )}

      {activeTab === 0 && chargeResponse && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Charge created successfully.
        </Alert>
      )}

      {activeTab === 1 && !!customerRequestView && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Request body sent by backend
          </Typography>
          <Typography component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {JSON.stringify(customerRequestView, null, 2)}
          </Typography>
        </Paper>
      )}

      {activeTab === 1 && !!customerFullResponseView && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Full response
          </Typography>
          <Typography component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {JSON.stringify(customerFullResponseView, null, 2)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
